import type { Conversation } from "@/types";

const minutes = (n: number) => new Date(Date.now() - n * 60_000).toISOString();
const hours = (n: number) => new Date(Date.now() - n * 60 * 60_000).toISOString();

/**
 * Realistic mock conversations for ARVID (T001).
 * Each conversation includes a timeline of pipeline events alongside
 * the chat messages — that's what makes the inbox feel like a CRM,
 * not a chat app.
 */
export const MOCK_CONVERSATIONS: Conversation[] = [
  {
    id: "c_001",
    channel: "whatsapp",
    status: "waiting",
    unread: 2,
    aiResponseSeconds: 8,
    updatedAt: minutes(4),
    preview:
      "Sí, mi caso es por una tarjeta de crédito que se me salió de control. Debo como 18 millones…",
    tags: [
      {
        id: "t_urg",
        label: "urgente",
        source: "human",
        createdAt: minutes(30),
      },
      {
        id: "t_fb",
        label: "facebook ads",
        source: "human",
        createdAt: minutes(33),
      },
    ],
    lead: {
      id: "l_001",
      name: "María Patiño",
      phone: "+57 311 482 1097",
      city: "Bogotá",
      debt: 18_400_000,
      stage: "calificado",
    },
    events: [
      {
        id: "e_001a",
        type: "lead_captured",
        label: "Lead capturado",
        detail: "Anuncio Facebook · Bogotá · CPL $4.300",
        createdAt: minutes(33),
      },
      {
        id: "e_001b",
        type: "ai_qualified",
        label: "Allison calificó el lead",
        detail: "Deuda > $5M · Persona natural · No comerciante",
        createdAt: minutes(31),
      },
      {
        id: "e_001c",
        type: "stage_changed",
        label: "Etapa: Nuevo → Calificado",
        createdAt: minutes(31),
      },
    ],
    messages: [
      {
        id: "m_001a",
        role: "user",
        source: "human",
        text: "Hola, vi su anuncio en Facebook sobre insolvencia de persona natural.",
        createdAt: minutes(32),
      },
      {
        id: "m_001b",
        role: "agent",
        source: "allison",
        text: "¡Hola María! Soy Allison de ARVID. Para saber si calificas, ¿podrías contarme un poco sobre tu situación? ¿Qué tipo de deudas tienes?",
        createdAt: minutes(31),
      },
      {
        id: "m_001c",
        role: "user",
        source: "human",
        text: "Sí, mi caso es por una tarjeta de crédito que se me salió de control. Debo como 18 millones y ya estoy en mora desde hace 4 meses.",
        createdAt: minutes(4),
      },
    ],
  },
  {
    id: "c_002",
    channel: "web",
    status: "ai",
    unread: 0,
    aiResponseSeconds: 6,
    updatedAt: minutes(12),
    preview: "Perfecto, te paso entonces el link para que agendes la cita…",
    tags: [
      {
        id: "t_emb",
        label: "embargo activo",
        source: "human",
        createdAt: minutes(17),
      },
      {
        id: "t_alto",
        label: "monto alto",
        source: "human",
        createdAt: minutes(15),
      },
    ],
    lead: {
      id: "l_002",
      name: "Diana Alzate",
      phone: "+57 320 778 4421",
      city: "Medellín",
      debt: 32_000_000,
      stage: "propuesta",
    },
    events: [
      {
        id: "e_002a",
        type: "lead_captured",
        label: "Lead capturado",
        detail: "Formulario web · Medellín",
        createdAt: minutes(20),
      },
      {
        id: "e_002b",
        type: "ai_qualified",
        label: "Allison calificó el lead",
        detail: "Embargo activo · Múltiples acreedores",
        createdAt: minutes(17),
      },
      {
        id: "e_002c",
        type: "proposal_sent",
        label: "Propuesta económica enviada",
        detail: "Plan A · 3 cuotas · $1.8M",
        createdAt: minutes(13),
      },
      {
        id: "e_002d",
        type: "stage_changed",
        label: "Etapa: Calificado → Propuesta",
        createdAt: minutes(12),
      },
    ],
    messages: [
      {
        id: "m_002a",
        role: "user",
        source: "human",
        text: "Necesito ayuda urgente, me están embargando la cuenta del Bancolombia.",
        createdAt: minutes(18),
      },
      {
        id: "m_002b",
        role: "agent",
        source: "allison",
        text: "Diana, tranquila. Con la Ley 2445 de 2025 el proceso de insolvencia suspende los embargos en curso. Cuéntame el monto total para validar.",
        createdAt: minutes(16),
      },
      {
        id: "m_002c",
        role: "user",
        source: "human",
        text: "Son 32 millones entre dos tarjetas y un libranza.",
        createdAt: minutes(14),
      },
      {
        id: "m_002d",
        role: "agent",
        source: "allison",
        text: "Perfecto, te paso entonces el link para que agendes la cita con la Dra. Margie. Es gratuita y dura 30 minutos.",
        createdAt: minutes(12),
      },
    ],
  },
  {
    id: "c_003",
    channel: "whatsapp",
    status: "human",
    unread: 0,
    updatedAt: minutes(47),
    preview: "Listo Dra., quedo atento al contrato y al PDF de poderes.",
    tags: [
      {
        id: "t_ref",
        label: "referido",
        source: "human",
        createdAt: hours(72),
      },
      {
        id: "t_vip",
        label: "vip",
        source: "human",
        createdAt: hours(48),
      },
    ],
    lead: {
      id: "l_003",
      name: "Jorge Henao",
      phone: "+57 314 226 9032",
      city: "Bogotá",
      debt: 45_700_000,
      stage: "ganado",
    },
    events: [
      {
        id: "e_003a",
        type: "lead_captured",
        label: "Lead capturado",
        detail: "Referido · Bogotá",
        createdAt: hours(72),
      },
      {
        id: "e_003b",
        type: "ai_qualified",
        label: "Allison calificó el lead",
        createdAt: hours(72),
      },
      {
        id: "e_003c",
        type: "appointment_booked",
        label: "Cita agendada con Margie",
        detail: "Hace 2 días · 10:30 AM",
        createdAt: hours(48),
      },
      {
        id: "e_003d",
        type: "won",
        label: "Cliente cerrado",
        detail: "Plan B · 6 cuotas · $2.4M",
        createdAt: hours(2),
      },
      {
        id: "e_003e",
        type: "stage_changed",
        label: "Etapa: Propuesta → Ganado",
        createdAt: hours(2),
      },
    ],
    messages: [
      {
        id: "m_003a",
        role: "user",
        source: "human",
        text: "Listo Dra., quedo atento al contrato y al PDF de poderes.",
        createdAt: minutes(47),
      },
    ],
  },
  {
    id: "c_004",
    channel: "instagram",
    status: "ai",
    unread: 1,
    aiResponseSeconds: 11,
    updatedAt: hours(2),
    preview: "¿Y eso cuesta cuánto? La verdad estoy bien apretado de plata…",
    tags: [
      {
        id: "t_bajo",
        label: "monto bajo",
        source: "human",
        createdAt: hours(2),
      },
      {
        id: "t_rev",
        label: "revisar después",
        source: "human",
        createdAt: hours(1),
      },
    ],
    lead: {
      id: "l_004",
      name: "Carlos Rincón",
      phone: "+57 312 904 5611",
      city: "Cali",
      debt: 9_200_000,
      stage: "nuevo",
    },
    events: [
      {
        id: "e_004a",
        type: "lead_captured",
        label: "Lead capturado",
        detail: "DM Instagram · Cali",
        createdAt: hours(2),
      },
    ],
    messages: [
      {
        id: "m_004a",
        role: "user",
        source: "human",
        text: "¿Y eso cuesta cuánto? La verdad estoy bien apretado de plata por eso es que estoy buscando insolvencia.",
        createdAt: hours(2),
      },
    ],
  },
  {
    id: "c_005",
    channel: "web",
    status: "resolved",
    unread: 0,
    updatedAt: hours(26),
    preview: "Mil gracias, ya me resolvieron la duda. Voy a hablarlo con mi esposa.",
    tags: [],
    lead: {
      id: "l_005",
      name: "Lucía Restrepo",
      city: "Medellín",
      debt: 14_000_000,
      stage: "calificado",
    },
    events: [
      {
        id: "e_005a",
        type: "lead_captured",
        label: "Lead capturado",
        detail: "Web · Medellín",
        createdAt: hours(28),
      },
      {
        id: "e_005b",
        type: "ai_qualified",
        label: "Allison calificó el lead",
        createdAt: hours(27),
      },
    ],
    messages: [
      {
        id: "m_005a",
        role: "user",
        source: "human",
        text: "Mil gracias, ya me resolvieron la duda. Voy a hablarlo con mi esposa.",
        createdAt: hours(26),
      },
    ],
  },
  {
    id: "c_006",
    channel: "telegram",
    status: "waiting",
    unread: 3,
    aiResponseSeconds: 14,
    updatedAt: minutes(8),
    preview:
      "Doctora me bloquearon la cuenta de Davivienda hoy 😔 ¿qué hago ya?",
    tags: [
      {
        id: "t_urg2",
        label: "urgente",
        source: "human",
        createdAt: hours(2),
      },
      {
        id: "t_cuenta",
        label: "cuenta bloqueada",
        source: "human",
        createdAt: minutes(8),
      },
    ],
    lead: {
      id: "l_006",
      name: "Andrés Quintero",
      phone: "+57 318 712 3344",
      city: "Bogotá",
      debt: 27_500_000,
      stage: "calificado",
    },
    events: [
      {
        id: "e_006a",
        type: "lead_captured",
        label: "Lead capturado",
        detail: "Telegram · Bogotá",
        createdAt: hours(4),
      },
      {
        id: "e_006b",
        type: "ai_qualified",
        label: "Allison calificó el lead",
        detail: "Embargo bancario activo",
        createdAt: hours(3),
      },
    ],
    messages: [
      {
        id: "m_006a",
        role: "user",
        source: "human",
        text: "Doctora me bloquearon la cuenta de Davivienda hoy 😔 ¿qué hago ya?",
        createdAt: minutes(8),
      },
    ],
  },
];

/* -------------------------------------------------------------------------- */
/*  Metrics                                                                   */
/* -------------------------------------------------------------------------- */

export interface InboxMetrics {
  total: number;
  needsAttention: number;
  inProgress: number;
  resolvedToday: number;
  aiHandled: number;
  humanHandled: number;
  /** Delta vs. yesterday — positive or negative integer. */
  deltaNeedsAttention: number;
  deltaAiHandled: number;
  /** Last 7 days, oldest → newest, for sparklines. */
  trendNeedsAttention: number[];
  trendAiHandled: number[];
}

export const MOCK_METRICS: InboxMetrics = {
  total: 24,
  needsAttention: 6,
  inProgress: 11,
  resolvedToday: 7,
  aiHandled: 18,
  humanHandled: 6,
  deltaNeedsAttention: -2,
  deltaAiHandled: 3,
  trendNeedsAttention: [4, 7, 5, 9, 8, 6, 6],
  trendAiHandled: [11, 13, 12, 16, 15, 17, 18],
};
